// Content for the "B-BBEE Masterclass" live-style slide deck.
// Transcribed faithfully from the real, already-delivered "Okiru B-BBEE
// Training Pack 2026" PowerPoint (130 source slides), redesigned for the web
// presentation system (see SlideRenderer.js) in the same shape as
// civils2000ClaudeProfessionals.js. Every statistic, percentage, date and
// claim below comes from that source deck; dense source slides (scorecard
// tables, ownership flow-through diagrams, EEA9 grading tables) are split
// across two or more deck slides rather than condensed, and the source's own
// recurring "Agenda" slide is reproduced at each of its real recurrences as
// a section-divider, exactly as it was used in the live delivery.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

export const manifest = applyOkiruTemplate(
  {
    id: 'bbbee-masterclass',
    title: 'Smart Data. Smarter Transformation.',
    subtitle: 'B-BBEE Masterclass 2026 · Okiru Learn',
    modules: [
      { index: 1, name: 'B-BBEE in the Context of South Africa' },
      { index: 2, name: 'Legislative Landscape' },
      { index: 3, name: 'AI in Context of Transformation' },
      { index: 4, name: 'Socio-Economic Development' },
      { index: 5, name: 'Preferential Procurement' },
      { index: 6, name: 'Enterprise and Supplier Development' },
      { index: 7, name: 'Skills Development' },
      { index: 8, name: 'Management Control' },
      { index: 9, name: 'Ownership' },
      { index: 10, name: 'YES4Youth' },
    ],
  },
  'okiru-light'
);

const AGENDA_COLUMNS_CORE = [
  [
    { time: '01', label: 'B-BBEE in the context of South Africa' },
    { time: '02', label: 'Legislative Landscape' },
    { time: '03', label: 'AI in Content of Transformation' },
    { time: '04', label: 'Pillar Analysis' },
    { time: '05', label: 'Socio Economic Development' },
  ],
  [
    { time: '06', label: 'Preferential Procurement' },
    { time: '07', label: 'Enterprise and Supplier Development' },
    { time: '08', label: 'Skills Development' },
    { time: '09', label: 'Management Control' },
    { time: '10', label: 'Ownership' },
    { time: '11', label: 'YES4Youth' },
  ],
];

const AGENDA_COLUMNS_WITH_CURRENT_STATE = [
  [
    { time: '01', label: 'B-BBEE in the context of South Africa' },
    { time: '02', label: 'Legislative Landscape' },
    { time: '03', label: 'AI in Content of Transformation' },
    { time: '04', label: 'Current State Analysis' },
    { time: '05', label: 'Pillar Analysis' },
  ],
  [
    { time: '06', label: 'Socio Economic Development' },
    { time: '07', label: 'Preferential Procurement' },
    { time: '08', label: 'Enterprise and Supplier Development' },
    { time: '09', label: 'Skills Development' },
    { time: '10', label: 'Management Control' },
    { time: '11', label: 'Ownership' },
    { time: '12', label: 'YES4Youth' },
  ],
];

const AGENDA_COLUMNS_WITH_SCENARIO_PLANNING = [
  [
    { time: '01', label: 'B-BBEE in the context of South Africa' },
    { time: '02', label: 'Legislative Landscape' },
    { time: '03', label: 'AI in Content of Transformation' },
    { time: '04', label: 'Current State Analysis' },
    { time: '05', label: 'Pillar Analysis' },
  ],
  [
    { time: '06', label: 'Socio Economic Development' },
    { time: '07', label: 'Preferential Procurement' },
    { time: '08', label: 'Enterprise and Supplier Development' },
    { time: '09', label: 'Skills Development' },
    { time: '10', label: 'Management Control' },
    { time: '11', label: 'Ownership' },
    { time: '12', label: 'YES4Youth' },
    { time: '13', label: 'Scenario Planning' },
  ],
];

export const slides = [
  // ---------------------------------------------------------------------
  // Front matter
  // ---------------------------------------------------------------------
  {
    id: 1, layout: 'title',
    kicker: 'Okiru Learn',
    title: 'Smart Data. Smarter Transformation.',
    lead: 'B-BBEE Masterclass 2026.',
    meta: ['South Africa in context', 'Every scorecard pillar', 'AI-enabled transformation'],
  },
  {
    id: 2, layout: 'statement',
    text: 'Smart data. Smarter transformation. B-BBEE done properly is an integrated, evidence-based process, not a certificate exercise.',
    footnote: 'That standard runs through every pillar in this masterclass.',
  },
  {
    id: 3, layout: 'agenda',
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },

  // ===========================================================================
  // Module 1: B-BBEE in the Context of South Africa
  // ===========================================================================
  {
    id: 4, layout: 'intro', module: 1,
    kicker: 'Module 01', title: 'B-BBEE in the Context of South Africa',
    lead: 'Growth, corruption, crime, unemployment, poverty, education, skills, healthcare and infrastructure, the real conditions B-BBEE was designed to change.',
    outcome: 'By the end you can explain why B-BBEE exists against South Africa’s actual socioeconomic backdrop, and what it was originally built to achieve.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 5, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'South Africa’s Growth Rate',
    lead: 'Real GDP growth in South Africa is projected to remain below 2% in 2023 and 2024, a level too weak to meet the country’s pressing socioeconomic demands.',
    body: 'The growth rate continues to lag behind population growth, limiting real improvements in living standards. To address poverty, unemployment and inequality, South Africa needs sustained growth above 3% over the long term.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 6, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Corruption and State Capture',
    lead: 'Corruption in South Africa erodes public trust, drains resources, and slows the pace of genuine transformation.',
    body: 'State capture scandals show how funds meant for development are diverted away from skills, jobs, and enterprise growth. Properly implemented, B-BBEE can act as a counterweight by directing investment into education, supplier development, and socio-economic upliftment, ensuring transformation benefits communities rather than corrupt networks.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 7, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'The South African Crime Rate',
    lead: 'South Africa continues to endure alarmingly high levels of violent crime, with more than 27,000 murders reported in the past year, placing it among the countries with the highest homicide rates globally (~45 per 100,000 people).',
    body: 'This persistent insecurity undermines transformation, as citizens in marginalized communities bear the brunt of violence, eroding confidence in state effectiveness and stifling economic opportunity. B-BBEE can contribute meaningfully by prioritizing community-based safety initiatives, supporting local enterprises in high-risk areas, and funding socio-economic upliftment that addresses root causes like inequality and unemployment.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 8, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'South Africa’s Unemployment',
    lead: 'Unemployment rose from 31.9% (Q4 2024) to 33.2% (Q2 2025); the expanded rate stands at 43.1%.',
    body: 'Youth are hit hardest: unemployment at 46.1%, with some provinces above 50% (Eastern Cape 54.3%, North West 58.8%). B-BBEE impact: targeting youth employment, skills funding, and enterprise support can reduce disparities and empower disadvantaged communities.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 9, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Ethnic Unemployment',
    lead: 'Black South Africans: unemployment at 37% (2025) vs 7–8% for white South Africans.',
    body: 'Black African women face the highest rates (~39.8%) compared to 17.3% for white women. B-BBEE imperative: move beyond optics, embed skills, enterprise support, and job creation with measurable outcomes to close racial gaps.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 10, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Poverty',
    lead: 'Poverty is racialised: 64% of Black South Africans live in poverty vs 1% of White South Africans.',
    body: 'Youth are hardest hit: 66% of Black African youth and 44% of Coloured youth live in poverty, compared to 16% Indian and 4% White youth. Transformation imperative: B-BBEE must move beyond compliance to drive education, social safety nets, and inclusive economic participation.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 11, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Education in South Africa',
    lead: 'Older South Africans: 96% of whites vs only 25% of blacks attained secondary education or higher.',
    body: 'Degrees (2023): 28.6% of whites vs 5.2% of blacks and 4.8% of Coloureds. Progress has been made (black African matric completion rose from 9.4% in 1996 to 32.4% in 2022), but the education gap remains stark and generational.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 12, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Skills in South Africa',
    lead: 'Entry doesn’t mean exit: Black learners dominate low-level placements, but access dwindles at higher levels.',
    body: 'Still falling short: tertiary attainment remains low (7.3%) and unequal, despite improvements in secondary completion. Mismatch in the market: critical thinking, tech, and leadership gaps persist, training must align with real-world needs.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 13, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Healthcare',
    lead: 'Insurance gap: only ~10% of Black South Africans hold private health coverage, compared to ~73% of White South Africans.',
    body: 'Distance divide: 14% of Black adults live more than 5 km from health services, vs. 4% for White adults. Skills shortfall: training and deploying health professionals in Black and rural communities must be a core focus of equitable reform.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 14, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'Infrastructure',
    lead: 'Uneven access across the board: Black households consistently lag behind white households in access to water, electricity and sanitation, even at similar income levels.',
    body: 'Rural neglect reinforces isolation: poor roads, broken telecoms, and limited energy services continue to marginalize remote communities, despite their human capital. Skills won’t mobilize without infrastructure: investment in skills must go hand-in-hand with infrastructure upgrades to connect capability with opportunity.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 15, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'So why should we try?',
    lead: 'Set against those numbers, South Africa still carries real, world-class strengths.',
    columns: 2,
    cards: [
      { title: 'World-class natural resources', description: 'South Africa is the world’s largest producer of platinum and a top producer of gold, manganese, and chromium, resources that power global industries.' },
      { title: 'A hub for renewable energy innovation', description: 'Our solar and wind corridors are among the best in the world, making South Africa Africa’s leader in renewable energy investment.' },
      { title: 'Globally recognized universities', description: 'The University of Cape Town, Wits, and Stellenbosch consistently rank among the top universities in Africa, producing graduates who compete globally.' },
      { title: 'Cutting-edge fintech growth', description: 'Cape Town and Johannesburg are home to thriving fintech ecosystems, leading Africa in mobile payments, blockchain applications, and digital banking solutions.' },
      { title: 'Rich cultural diversity', description: 'With 11 official languages and countless traditions, South Africa’s cultural blend is unique, shaping everything from cuisine and fashion to music and film.' },
      { title: 'Africa’s best transport and logistics hub', description: 'Durban and Cape Town ports rank among the busiest and most advanced in Africa, making South Africa the gateway for regional trade.' },
      { title: 'Leading in medical research', description: 'South African scientists pioneered the world’s first heart transplant, and our researchers continue to lead breakthroughs in HIV/AIDS and TB treatment.' },
      { title: 'Globally competitive wine industry', description: 'Our Western Cape vineyards are ranked among the best globally, exporting award-winning wines to over 100 countries.' },
      { title: 'Home to world-renowned wildlife reserves', description: 'Kruger National Park and numerous private reserves attract millions of tourists annually, contributing to conservation and sustainable tourism.' },
      { title: 'Strong entrepreneurial spirit', description: 'Small and medium businesses account for over 60% of employment, with a vibrant startup culture making South Africa Africa’s entrepreneurial engine.' },
    ],
  },
  {
    id: 16, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'B-BBEE and the NDP: Alignment with NDP 2030 Goals',
    lead: 'B-BBEE drives inclusive growth, echoing the NDP’s vision for reduced inequality, increased employment, and sustainable development.',
    body: 'Both frameworks stress skills development, entrepreneurship, and expanding opportunities for historically disadvantaged groups.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 17, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'B-BBEE and the NDP: Shared Priorities',
    lead: 'B-BBEE is not just a compliance mechanism but a key tool to achieve the NDP’s socio-economic transformation goals. Effective implementation builds business resilience, investor confidence, and long-term national competitiveness.',
    columns: 3,
    cards: [
      { title: 'Job Creation', description: 'The NDP aims to reduce unemployment to 14% by 2030; B-BBEE promotes enterprise & supplier development to expand employment.' },
      { title: 'Education & Skills', description: 'Both prioritize upskilling youth and workers for a knowledge economy.' },
      { title: 'Equity & Inclusion', description: 'Address racial, gender, and spatial inequalities by broadening economic participation.' },
    ],
  },
  {
    id: 18, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'Impact of AI on the South African Future: Key Opportunities',
    columns: 2,
    cards: [
      { title: 'Economic Growth', description: 'AI could add up to $136 billion (≈ R2.5 trillion) to South Africa’s economy by 2030 through productivity gains and new industries.' },
      { title: 'Skills & Jobs', description: 'Creates demand for digital skills, data science, robotics, and AI ethics, while enabling reskilling programs aligned with B-BBEE and NDP 2030.' },
      { title: 'Public Services', description: 'Smarter healthcare diagnostics, digitalised education, predictive policing, and efficient public administration.' },
      { title: 'Business Advantage', description: 'Enhances competitiveness in financial services, mining, retail, and agriculture with automation and analytics.' },
    ],
  },
  {
    id: 19, layout: 'comparison', module: 1,
    kicker: 'Module 01', title: 'Impact of AI on the South African Future: Key Risks and Strategic Imperatives',
    columns: [
      { heading: 'Key Risks', items: ['Job Displacement: low- to mid-skill jobs most at risk without parallel reskilling efforts.', 'Bias & Inequality: risk of AI entrenching racial and economic divides if training data reflects systemic inequalities.', 'Digital Divide: without access to connectivity and infrastructure, rural and marginalized groups may be left behind.'] },
      { heading: 'Strategic Imperatives', items: ['Align AI adoption with B-BBEE to ensure inclusivity.', 'Invest in STEM education and digital literacy for youth.', 'Develop ethical AI frameworks to balance innovation with accountability.'] },
    ],
  },
  {
    id: 20, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'B-BBEE’s Human Capital Vertical (RCOGP, 2015)',
    lead: 'Equitable economic opportunities, ownership, management control, skills development, learnerships and socio-economic development make up the Human Capital Vertical of the scorecard.',
    columns: 4,
    cards: [
      { title: 'Ownership — 25 points', description: 'The percentage share of economic benefits of black South Africans in the company.' },
      { title: 'Management Control — 19 points', description: 'Representation in board decision making, executive management, senior, middle & junior employee base.' },
      { title: 'Skills Development — 20 + 5 points', description: 'Certified training of black people (not only employees), measured as a ratio of training spend to payroll. The "+5" is the ‘Ships’, practical work-based learning of black people measured as a ratio of headcount, the talent pipeline.' },
      { title: 'Socio-Economic Development — 5 points', description: 'Grass-roots economic access issues such as health, and NOT education, for example future employees and future customers.' },
    ],
  },
  {
    id: 21, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'And a Value Chain Horizontal Aimed at Creating Jobs',
    columns: 3,
    cards: [
      { title: 'Enterprise Development — 5 + 2', description: 'Support to black owned (50%) small & micro suppliers to enable their development to become future suppliers, distributors or customers.' },
      { title: 'Supplier Development — 10', description: 'Support to black owned (50%) small & micro suppliers to enable their development to become efficient.' },
      { title: 'Preferential Procurement — 27 + 2', description: 'Procurement from Empowering suppliers as a proportion of total procurement relative to B-BBEE level, size and black ownership, to reduce twin economies.' },
    ],
  },
  {
    id: 22, layout: 'statement', module: 1,
    text: '"South Africa stood on the threshold of a new digital era." President Cyril Ramaphosa, 2024 World Economic Forum address.',
    footnote: 'He cautioned that the rise of artificial intelligence and ICT would create two very different futures: one for those with access to digital skills and connectivity, and another for those left behind in the analogue economy. For many, this digital divide is simply the new face of inequality.',
  },
  {
    id: 23, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'Deliver Sustainable Transformation for Your Company',
    lead: 'Sustainable transformation runs on strategy, operations & AI, and commitment.',
    columns: 3,
    cards: [
      { title: 'Strategy: Align Stakeholders', description: 'Align internal and external stakeholders to commitments and conversations. Trust is a competitive advantage.' },
      { title: 'Operations & AI', description: 'Measurement and tracking of process. Drive action by integrating AI execution deep into the organization’s growth strategy for sustainable, efficient execution.' },
      { title: 'Commitment: Bold Leadership', description: 'Align business to purpose. Be problem-centric versus optimistic, to create a positive ROI from transformation.' },
    ],
  },
  {
    id: 24, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'Some Success Examples',
    columns: 3,
    cards: [
      { title: 'MTN spends R33 billion on Black-owned SMME suppliers', description: 'In 2024, MTN Group and MTN South Africa spent R33.4 billion with Black-owned small, medium, and micro enterprises. This massive investment reinforced their Level 1 B-BBEE contributor status and substantially expanded economic inclusion in their supply chain.' },
      { title: 'Sasol launches R21 billion "Khanyisa" B-BBEE ownership scheme', description: 'In 2018, Sasol implemented Khanyisa, a landmark R21 billion B-BBEE ownership structure aimed at achieving 25% direct and indirect black ownership in Sasol South Africa. This strategy granted over 200,000 Black South Africans a stake, through employees, public investors and empowerment groups.' },
      { title: 'Woolworths injects R2 million into young entrepreneurs', description: 'In June 2024, Woolworths celebrated Youth Month by investing R2 million in its Youth Makers competition, supporting 15 up-and-coming entrepreneurs with local retail leadership and funding. Winners received mentorship, strategic access to Woolworths’ consumer base, and R130,000 in grants each.' },
    ],
  },
  {
    id: 25, layout: 'statement', module: 1,
    text: '"An integrated and coherent socioeconomic process that directly contributes to the economic transformation of South Africa and brings about significant increases in the number of black people that manage, own and control the country’s economy, as well as significant decreases in inequalities." The objective of B-BBEE in 2007.',
    footnote: 'B-BBEE was not originally about poverty alleviation but about strengthening a middle class.',
  },

  // ===========================================================================
  // Module 2: Legislative Landscape
  // ===========================================================================
  {
    id: 26, layout: 'agenda', module: 2,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },
  {
    id: 27, layout: 'intro', module: 2,
    kicker: 'Module 02', title: 'Legislative Landscape',
    lead: 'The Generic Codes, the scorecard weightings, the recognition levels, the definitions that decide who counts, and the Acts that sit behind all of it.',
    outcome: 'By the end you can read the Generic Codes scorecard, place a company on the recognition-level table, and apply the Codes’ own definitions correctly.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 28, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Revised Codes of Good Practice (Generic Codes)',
    lead: 'Generic and Generic-Large scorecards total 111 points plus 9 bonus points, spread across seven elements.',
    columns: 4,
    cards: [
      { title: 'Ownership — 25', description: '' },
      { title: 'Management Control — 19', description: '' },
      { title: 'Skills Development — 20 + 5', description: '' },
      { title: 'Preferential Procurement — 27 + 2', description: '' },
      { title: 'Supplier Development — 10', description: '' },
      { title: 'Enterprise Development — 5 + 2', description: '' },
      { title: 'Socioeconomic Development — 5', description: '' },
      { title: 'Total — 111 + 9 bonus points', description: '' },
    ],
  },
  {
    id: 29, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'Revised Codes of Good Practice: B-BBEE Recognition Levels',
    lead: 'The scorecard total converts into one of nine B-BBEE status levels, each carrying its own procurement recognition percentage.',
    templateAsset: 'icon-consistency',
    items: [
      { title: 'Level One Contributor', description: '> 100 points on the scorecard → 135% B-BBEE recognition level.' },
      { title: 'Level Two Contributor', description: '≥ 95 but < 100 points → 125%.' },
      { title: 'Level Three Contributor', description: '≥ 90 but < 95 points → 110%.' },
      { title: 'Level Four Contributor', description: '≥ 80 but < 90 points → 100%.' },
      { title: 'Level Five Contributor', description: '≥ 75 but < 80 points → 80%.' },
      { title: 'Level Six Contributor', description: '≥ 70 but < 75 points → 60%.' },
      { title: 'Level Seven Contributor', description: '≥ 55 but < 70 points → 50%.' },
      { title: 'Level Eight Contributor', description: '≥ 40 but < 55 points → 10%.' },
      { title: 'Non-Compliant Contributor', description: '< 40 points on the scorecard → 0%.' },
    ],
  },
  {
    id: 30, layout: 'lead', module: 2,
    kicker: 'Module 02 · SANAS guidance', title: 'NPAT Calculation',
    lead: 'If NPAT margin is less than 25% of the industry norm, the target now becomes turnover based.',
    body: 'Calculate the Indicative Profit Margin (IPM = NPAT / Turnover). The NPAT figure used must be the most recent NPAT (not older than 5 years) which is at least 25% of the industry norm. Step 5.1: slot the Indicative Profit Margin into the formula element target (1% or 2%) x IPM (actual figure, not 25% of IPM) x Turnover = New Target. Step 5.2: if the NPAT margin is less than 25% of the industry norm, stretching back longer than 5 years, the target becomes 25% of the latest industry norm.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
    callout: { label: 'Source', text: 'From Yuneal Padavachy, Accreditation Manager at SANAS.' },
  },
  {
    id: 31, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'Priority Elements: Sub-Minimum Requirements',
    lead: 'Ownership, Skills Development, and Enterprise & Supplier Development are Priority Elements, each carrying its own 40% sub-minimum. A Qualifying Small Enterprise must choose between Skills Development or ESD.',
    items: [
      { title: 'Ownership', description: '40% sub-minimum of the net value points: 3.2 out of 8. Applies to Large Enterprises and Qualifying Small Enterprises.' },
      { title: 'Skills Development', description: '40% sub-minimum of the total points: 8 out of 20. Applies to Large Enterprises (QSEs must choose between SD or ESD).' },
      { title: 'Enterprise & Supplier Development — Preferential Procurement', description: '40% sub-minimum of the points: 10.8 out of 27. Applies to Large Enterprises.' },
      { title: 'Enterprise & Supplier Development — Supplier Development', description: '40% sub-minimum of the points: 4 out of 10.' },
      { title: 'Enterprise & Supplier Development — Enterprise Development', description: '40% sub-minimum of the points: 2 out of 5.' },
    ],
  },
  {
    id: 32, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'The Discounting Principle',
    lead: 'Non-compliance with a 40% sub-minimum does not simply cost the points, it costs a whole level.',
    body: 'The actual points scored by the measured entity, and the consequent level the measured entity would have achieved were it not for non-compliance with the 40% sub-minimum requirements, will be recognised by the verification agency. Notwithstanding that recognition, the measured entity’s B-BBEE status level will be discounted by one level down until the next applicable verification period in which the measured entity can demonstrate compliance with the 40% sub-minimum requirements, at which point the recorded level will become the applicable ratings level for that measured entity in that verification period.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 33, layout: 'numbered', module: 2,
    kicker: 'Module 02 · Definitions', title: 'Definitions',
    items: [
      { title: 'B-BBEE', description: 'Broad-Based Black Economic Empowerment.' },
      { title: 'Robust B-BBEE compliant suppliers', description: 'All suppliers with a B-BBEE scorecard of at least 60%, including Large Enterprises, Black Enterprises, Black Women Owned Enterprises, Qualifying Small Enterprises (QSE) and Exempt Micro Enterprises (EME).' },
      { title: 'Dilutive B-BBEE compliant suppliers', description: 'All suppliers with a B-BBEE scorecard below 60%, including Large Enterprises, Black Enterprises, Black Women Owned Enterprises, Qualifying Small Enterprises (QSE) and Exempt Micro Enterprises (EME).' },
      { title: 'Black owned Enterprise (BO)', description: 'A legal entity with 51% or more of its equity owned and controlled by Black People, meaning Africans, Coloureds and Indians who are citizens of South Africa by birth or descent, or who became citizens of South Africa by naturalisation before 27 April 1994 (or on or after that date, if they would have been entitled to acquire citizenship by naturalisation prior to it).' },
    ],
  },
  {
    id: 34, layout: 'numbered', module: 2,
    kicker: 'Module 02 · Definitions', title: 'Definitions, Continued',
    items: [
      { title: 'Black Women Owned Enterprises (BWO)', description: 'Entities which are 30% owned by Black Women. "Black woman" means African, Coloured and Indian women who are citizens of South Africa by birth or descent, or who became citizens by naturalisation before 27 April 1994 (or on or after that date, if entitled to naturalisation prior to it).' },
      { title: 'Direct Suppliers', description: 'Suppliers whose products or services become a component in the manufacturing process.' },
      { title: 'Indirect Suppliers', description: 'Suppliers who are not direct suppliers, including capex expenditure.' },
      { title: 'Large Enterprise', description: 'Any entity which, at the time of its procurement by a Measured Entity, has a turnover greater than R50 million at the date of first procurement in relation to the undertaking of the programme activities.' },
    ],
  },
  {
    id: 35, layout: 'numbered', module: 2,
    kicker: 'Module 02 · Definitions', title: 'Definitions, Continued',
    items: [
      { title: 'Exempted Micro Enterprise (EME)', description: 'An entity which, at the time of its procurement by a Measured Entity, has a turnover of between 0 and R10 million at the date of first procurement in relation to the undertaking of the programme activities.' },
      { title: 'Qualifying Small Enterprise (QSE)', description: 'An entity which, at the time of its procurement by a Measured Entity, has a turnover of between R10 million and R50 million at the date of first procurement in relation to the undertaking of the programme activities.' },
      { title: 'Preferential Procurement', description: 'The extent to which a measured entity leans towards procurement of goods and services from suppliers that support the preferential procurement targets.' },
      { title: 'B-BBEE Procurement spend', description: 'The weighted B-BBEE spend based on the B-BBEE level of the supplier’s scorecard, measured on the B-BBEE Codes of Good Practice (2007) for all key suppliers, and on the Revised Codes of Good Practice (2013) for all non-key suppliers.' },
      { title: 'Enterprise Development contributions', description: 'Contributions given to Exempted Micro-Enterprises or Qualifying Small Enterprises which are at least 51% Black owned or at least 51% Black women owned.' },
    ],
  },
  {
    id: 36, layout: 'numbered', module: 2,
    kicker: 'Module 02 · Definitions', title: 'Total Measured Procurement Spend (TMPS) and Recognition Levels',
    items: [
      { title: 'Total Measured Procurement Spend (TMPS)', description: 'The monetary spend on the procurement of goods and services, excluding imported goods and services, taxation, salaries and wages, and (per the B-BBEE legislation) pass-through third-party procurement, procurement from government monopolies, and inter-group procurement.' },
      { title: 'Pass-through third-party procurement', description: 'All procurement for a third party or a client that is recorded as an expense in the third party’s or client’s annual financial statements but is not recorded as such in the Measured Entity’s own financial statements.' },
      { title: 'B-BBEE Recognition Levels', description: 'The levels of recognition of achievement in respect of vendors, measured in terms of the DTI B-BBEE Codes of Good Practice (2007) and the Revised Codes of Good Practice (2013), applied to key and non-key vendors under the "New Codes".' },
    ],
  },
  {
    id: 37, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'The Legislative Landscape: Acts Regulating B-BBEE',
    lead: 'Examples of legislation regulating Black Economic Empowerment.',
    items: [
      { title: 'Constitution of the Republic of South Africa', description: 'Act 108 of 1996.' },
      { title: 'The Competition Act', description: 'Act 89 of 1998.' },
      { title: 'The National Empowerment Fund Act', description: 'Act 105 of 1998.' },
      { title: 'Skills Development Act and Regulations', description: 'Act 97 of 1998.' },
      { title: 'Employment Equity Act', description: 'Act 55 of 1998.' },
      { title: 'Skills Development Levies Act and Regulations', description: 'Act 9 of 1999.' },
      { title: 'Public Finance Management Act (PFMA) and Framework for Supply Chain Management Regulations', description: 'Act 1 of 1999.' },
      { title: 'The Preferential Procurement Policy Framework Act', description: 'Act 5 of 2000.' },
      { title: 'The Broad-Based Black Empowerment Act', description: 'Act 53 of 2003.' },
    ],
  },
  {
    id: 38, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'How the Legislative Hierarchy Fits Together',
    lead: 'The Constitution sits at the top of the hierarchy that produces the B-BBEE Codes.',
    body: 'Beneath the Constitution sit the Mineral & Petroleum Resource Development Act and Mining Charter, the Preferential Procurement Policy Framework Act (PPPFA) and its Regulations, and the Employment Equity Act. These feed into the Sector Codes and Generic Codes, alongside the Skills Development Act. The BEE Act itself then produces the B-BBEE Strategy and its Interpretive Guide.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 39, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Fronting',
    lead: 'The BEE Amendment Act of 2014 defines "fronting" and categorises it as a criminal offence, setting out related penalties which may include fines and imprisonment.',
    body: 'Fronting includes a transaction, arrangement, or conduct that directly or indirectly undermines or frustrates BEE.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 40, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'What Counts as Fronting',
    lead: 'Fronting includes, but is not limited to, a BEE transaction:',
    items: [
      { title: 'Discouraging real participation', description: 'One in terms of which Black persons who are appointed to an enterprise are discouraged or inhibited from substantially participating in the core activities of that enterprise.' },
      { title: 'Benefits that don’t flow as documented', description: 'One in terms of which the economic benefits received as a result of the BEE status of an enterprise do not flow to black people in the ratio specified in the relevant legal documentation.' },
      { title: 'A relationship without real economic benefit', description: 'One involving the conclusion of a legal relationship with a black person for the purpose of the enterprise achieving a certain level of BEE compliance, without granting that black person the economic benefits reasonably expected to be associated with their status or position.' },
      { title: 'An agreement engineered to enhance BEE status', description: 'One involving an agreement with another enterprise to achieve and enhance BEE status where there are significant limitations on the identity of suppliers, service providers, clients or customers; where the maintenance of business operations is reasonably considered improbable given the resources involved; and/or where the terms and conditions were not negotiated prior to implementation on a fair and reasonable basis.' },
    ],
  },
  {
    id: 41, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Offences and Penalties for Fronting',
    lead: 'A person is guilty of an offence if they knew (or in their position should have known) of misrepresentation or attempted misrepresentation of the BEE status of an enterprise.',
    body: 'Any person convicted of an offence under this Act is liable to a fine and/or imprisonment for a period not exceeding 10 years. In the case of an enterprise, the fine is 10% of that enterprise’s annual turnover. In addition, any person and/or entity convicted of an offence shall be banned from contracting or transacting any business with any organ of state and/or public entity, and shall be entered into the National Treasury register of tender defaulters.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 42, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Fronting Indicators',
    columns: 3,
    cards: [
      { title: 'Window Dressing', description: 'Black participation that exists on paper but not in substance.' },
      { title: 'Benefit Diversion', description: 'Economic benefits that do not actually flow to the black participants they are attributed to.' },
      { title: 'Opportunistic Intermediaries', description: 'Intermediary structures used to claim BEE status without conferring real participation.' },
    ],
  },
  {
    id: 43, layout: 'comparison', module: 2,
    kicker: 'Module 02', title: 'Definition of Black People',
    columns: [
      { heading: 'Includes', items: ['African, Indian, Coloured South African citizens by birth or descent.', 'Naturalised before 27 April 1994 (or naturalised after, but would have been entitled before that date).'] },
      { heading: 'Excludes', items: ['White persons — South African.', 'All foreign nationals — black or white.', 'Chinese persons (unless naturalised prior to 1994).'] },
    ],
  },
  {
    id: 44, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'Black Designated Group Definition',
    items: [
      { title: 'Unemployed people', description: 'Not attending and not required by law to attend an educational institution, and not awaiting admission to one.' },
      { title: 'Black youth', description: 'As defined in the National Youth Commission Act of 1996.' },
      { title: 'Black people with disabilities', description: 'As defined in the Code of Good Practice on employment of people with disabilities issued under the Employment Equity Act.' },
      { title: 'Black people in rural and under-developed areas', description: '' },
      { title: 'Black military veterans', description: 'Who qualify to be called a military veteran in terms of the Military Veterans Act 18 of 2011.' },
    ],
  },
  {
    id: 45, layout: 'comparison', module: 2,
    kicker: 'Module 02', title: 'New Employment Equity Amendment Bill',
    lead: 'A certificate of compliance will only be issued to employers that meet all three conditions.',
    columns: [
      { heading: 'Certificate of compliance requires', items: ['Applicable sectorial targets achieved, or reasonable grounds raised for non-compliance.', 'Most recent employment equity report submitted.', 'No finding within the previous twelve months of unfair discrimination or paying below the national minimum wage.'] },
      { heading: 'Key changes in the bill', items: ['Regulation of sector-specific numerical EE targets by the Department of Employment and Labour.', 'Introduction of an EE Compliance Certificate, a statutory requirement for recognition of points under the Management Control pillar of the BEE scorecard.', 'State contracts and other supplier opportunities directly linked to a company’s EE compliance.'] },
    ],
  },
  {
    id: 46, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'New Employment Equity Amendment Bill: Business Implications',
    items: [
      { title: 'More frequent inspections', description: 'The Minister can designate representatives within the Department of Labour to act on their behalf when determining compliance.' },
      { title: 'Annual audit', description: 'Issuance of a certificate means the entity will get audited or verified on a yearly basis by the Department of Labour.' },
      { title: 'Sectoral employment targets become binding', description: 'Employers will be forced to employ either a set number or set percentage of staff per occupational level based on sectoral targets, or risk being non-compliant.' },
      { title: 'A contravention if targets are missed', description: 'Failure to comply with sectoral numerical targets, or to provide reasonable grounds for non-compliance to the satisfaction of the Minister, will be a contravention of the Act.' },
    ],
  },
  {
    id: 47, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'New Employment Equity Amendment Bill: Business Implications, Continued',
    items: [
      { title: 'Large financial penalties', description: 'Fines for breaching regulations are the higher of R1.5 million or 2% of the company’s turnover.' },
      { title: 'Direct risk to your BEE scorecard', description: 'Failure to obtain the compliance certificate poses a direct risk to the Measured Entity’s BEE strategy and scorecard: no points will be awarded under the Management Control pillar, irrespective of representation in the business.' },
      { title: 'The EE Act takes precedence', description: 'From an Employment Equity lens, the Employment Equity Act takes precedence over any provisions that may exist in the BEE Codes.' },
    ],
  },
  {
    id: 48, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Definition of a 51% Black-Owned Company',
    lead: 'A 51% Black-Owned Company is an entity in which Black people hold at least 51% voting rights, hold at least 51% economic interest, and the entity has earned full net value points.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 49, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Three Types of Enterprises Per the Codes',
    lead: 'Classification is based on revenue / turnover per annum, per the most recent financial year.',
    columns: 3,
    cards: [
      { title: 'EME (Exempted Micro Enterprise)', description: 'Less than R10 million turnover.' },
      { title: 'QSE (Qualifying Small Enterprise)', description: 'Between R10 million and R50 million turnover.' },
      { title: 'Large / Generic', description: 'More than R50 million turnover.' },
    ],
  },
  {
    id: 50, layout: 'comparison', module: 2,
    kicker: 'Module 02', title: 'EME and QSE: Ownership Band, Recognition Level, Audit Requirement',
    columns: [
      { heading: 'EME (turnover < R10 million per annum)', items: ['100% black ownership → flow-through, Level 1, affidavit.', '> 51% but < 100% black ownership → flow-through, Level 2, affidavit.', '0% but < 51% black ownership → Level 4, affidavit.'] },
      { heading: 'QSE (turnover R10m–R50m per annum)', items: ['100% black ownership → flow-through, Level 1, affidavit.', '> 51% but < 100% black ownership → flow-through, Level 2, affidavit.', '0% but < 51% black ownership → based on audit outcome (QSE Scorecard).'] },
    ],
  },
  {
    id: 51, layout: 'lead', module: 2,
    kicker: 'Module 02 · Worked example', title: 'Modified Flow-Through',
    lead: 'Where in the chain of ownership, black people have a flow-through level of participation of at least 51%, and then only once in the entire ownership structure of the Measured Entity, such black participation may be treated as if it were 100% Black.',
    body: 'Structure: Black persons hold 51% of Holding Co. A, which holds 51% of the Measured Entity (non-black persons hold the remaining 49% at each level). Ordinary flow-through: 51% x 51% = 26% black shareholding of the Measured Entity. Modified flow-through: because the 51% threshold is met at the first level, that participation is treated as 100% Black at that level, so 51% x 100% = 51% black shareholding of the Measured Entity.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 52, layout: 'timeline', module: 2,
    kicker: 'Module 02', title: 'Timelines: The FY2025 / FY2026 Cycle',
    templateAsset: 'take-action',
    steps: [
      { era: 'Jan ’25', title: 'FY 2025 begins', description: 'Spend for the whole year, FY 2025.' },
      { era: 'Dec ’25', title: 'FY 2025 ends', description: 'The full year’s spend closes.' },
      { era: 'Current', title: 'Verification', description: 'BEE verification based on results and procurement for FY 2025.' },
      { era: 'Jan ’26 – Dec ’26', title: 'FY 2026', description: 'Continuous planning for FY 2026 to improve the score.' },
    ],
  },
  {
    id: 53, layout: 'numbered', module: 2,
    kicker: 'Module 02', title: 'Procurement Principles',
    items: [
      { title: 'Based on the fiscal year', description: 'Based on spend during the fiscal year / financial year, the whole year’s spend (01 Jan 2025 to 31 Dec 2025).' },
      { title: 'Includes all spend', description: 'Core and non-core, on any AP system, debit orders, credit cards, etc.' },
      { title: 'Excludes', description: 'Imports / foreign suppliers, and other exclusions per the TMPS calculation.' },
      { title: 'VAT and accrual basis', description: 'All amounts exclude VAT. Amounts are based on invoices accrued (not necessarily paid), linked to the financials.' },
      { title: 'Valid BEE certificates', description: 'Ones that expired within the relevant financial period, or became valid after the start of the financial period.' },
    ],
  },
  {
    id: 54, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'How Are You Going to Manage the Process of Transformation?',
    columns: 2,
    cards: [
      { title: 'EXCO', description: '30-day cycles of tactical & strategic analysis, brainstorming for strategic fit, and process & project review.' },
      { title: 'The BBBEE Committee', description: 'Building a current state: assessing systems, querying suppliers, understanding transformation. Surfacing ideas: subject matter experts and project managers driving the project plan.' },
    ],
  },

  // ===========================================================================
  // Module 3: AI in Context of Transformation
  // ===========================================================================
  {
    id: 55, layout: 'agenda', module: 3,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_WITH_CURRENT_STATE,
  },
  {
    id: 56, layout: 'intro', module: 3,
    kicker: 'Module 03', title: 'AI in Content of Transformation',
    lead: 'Why AI matters for transformation delivery, and where it actually changes the numbers.',
    outcome: 'By the end you can explain, in plain terms, what AI-enabled transformation looks like compared with the traditional, manual version.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 57, layout: 'statement', module: 3,
    text: '"Transformation without AI risks being incremental. Transformation with AI can be exponential."',
    footnote: 'AI as a Driver of Transformation.',
  },
  {
    id: 58, layout: 'comparison', module: 3,
    kicker: 'Module 03', title: 'Why AI Matters in Transformation',
    columns: [
      { heading: 'Traditional Transformation', items: ['Data-Heavy: massive amounts of learner records, supplier certificates, and payroll evidence handled manually, creating inefficiencies and errors.', 'Slow: manual processes delay submissions, audits, and reporting cycles, reducing responsiveness to regulatory or business changes.', 'Compliance-Focused: efforts are often limited to "ticking boxes" for B-BBEE, EE, or Skills Development, without translating into long-term capability or business value.'] },
      { heading: 'AI-Enabled Transformation', items: ['Faster: automated data collection, validation, and reporting streamline compliance workflows, saving time and resources.', 'Scalable: can process thousands of records across multiple business units or suppliers without adding headcount.', 'Predictive: uses analytics to forecast compliance risks, identify skills gaps, and model outcomes before decisions are made.'] },
    ],
  },
  {
    id: 59, layout: 'lead', module: 3,
    kicker: 'Module 03', title: 'The Transformation Pain Points',
    lead: 'The traditional way of running transformation is data-heavy, slow, and compliance-focused.',
    body: 'Massive volumes of learner records, supplier certificates and payroll evidence are still handled manually in most organisations, which creates inefficiencies and errors, delays submissions, audits and reporting cycles, and keeps effort limited to ticking boxes rather than building long-term capability or business value.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 60, layout: 'lead', module: 3,
    kicker: 'Module 03', title: 'Where AI Creates Impact',
    lead: 'AI-enabled transformation is faster, scalable, and predictive.',
    body: 'Automated data collection, validation, and reporting streamline compliance workflows and save time and resources. The same tooling can process thousands of records across multiple business units or suppliers without adding headcount, and use analytics to forecast compliance risks, identify skills gaps, and model outcomes before decisions are made.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 61, layout: 'lead', module: 3,
    kicker: 'Module 03', title: 'Case Examples in Transformation',
    lead: 'The real-world cases already covered in this masterclass are the proof points for AI- and data-enabled transformation at scale.',
    body: 'MTN’s R33.4 billion spend with Black-owned SMMEs, Sasol’s R21 billion Khanyisa ownership scheme, and Woolworths’ R2 million Youth Makers investment all depended on the underlying data, tracking and reporting discipline needed to evidence transformation at that scale.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 62, layout: 'statement', module: 3,
    text: '"AI doesn’t replace people. It frees them." "AI as a tool, not a replacement."',
    footnote: 'The Human Factor.',
  },

  // ===========================================================================
  // Module 4: Socio Economic Development
  // ===========================================================================
  {
    id: 63, layout: 'agenda', module: 4,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_WITH_SCENARIO_PLANNING,
  },
  {
    id: 64, layout: 'intro', module: 4,
    kicker: 'Module 04', title: 'Socio Economic Development',
    lead: 'What SED measures, how it is scored, calculated, and best practised, and what verification agencies check for.',
    outcome: 'By the end you can calculate an SED target from NPAT, name the five qualifying criteria, and describe SED best practice.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 65, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'What Is Socio-Economic Development?',
    lead: 'Socio-Economic Development measures contributions that improve the lives of Black people.',
    body: 'These include education, community upliftment, social impact projects, and skills for employability.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 66, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'Socioeconomic Development (SED): The Target',
    lead: 'SED carries 5 points, scored on the annual value of all socioeconomic contributions by the entity as a percentage of the target.',
    body: 'Target spend is 1.0% of NPAT. Contributions with more than 75% black beneficiaries qualify for a full Rand-for-Rand contribution.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 67, layout: 'numbered', module: 4,
    kicker: 'Module 04', title: 'Five Qualifying Criteria for Eligibility Under SED',
    items: [
      { title: 'Beneficiaries are not your employees', description: '' },
      { title: 'Line of sight on beneficiaries', description: 'Who are the beneficiaries needs to be identifiable and trackable.' },
      { title: 'Cost can take two forms', description: 'Cost to company, or opportunity cost.' },
      { title: 'Promotes sustainable access to the economy', description: 'For the beneficiaries.' },
      { title: 'Not formal academic or certified training', description: 'That falls under Skills Development instead.' },
    ],
  },
  {
    id: 68, layout: 'lead', module: 4,
    kicker: 'Module 04 · Worked example', title: 'Socioeconomic Development: Calculations',
    lead: 'NPAT = R125,000 (from the financial statements). 1% NPAT = R125,000 x 1% = R1,250 (the SED target).',
    body: 'SED Score = (Achieved / 1% NPAT) x Weight. In other words: achieved total spend, divided by the 1% NPAT target, multiplied by the element’s weight, gives the SED score.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 69, layout: 'cards', module: 4,
    kicker: 'Module 04', title: 'Socioeconomic Development: Best Practice',
    lead: 'Best practice runs across people, products, process, positioning and partners, with the purpose of building future employees.',
    columns: 3,
    cards: [
      { title: 'Involve the full organisation', description: 'Not just the compliance or CSI team.' },
      { title: 'Invest strategically', description: 'Treat SED spend as a strategic investment, not a donation.' },
      { title: 'Collaborate and partner', description: 'Look for exposure to future clients and replicable models.' },
    ],
  },
  {
    id: 70, layout: 'numbered', module: 4,
    kicker: 'Module 04', title: 'What Contributes to Socioeconomic Development Targets: Grants and Costs',
    lead: 'Grant and related contributions recognise different types of support at different benefit factors.',
    items: [
      { title: 'Grant contribution', description: 'Full grant amount recognised at a 100% benefit factor.' },
      { title: 'Direct cost incurred supporting approved projects or SED', description: 'Verifiable cost (monetary and non-monetary) at a 100% benefit factor.' },
      { title: 'Discounts beyond normal business practice, supporting approved projects or SED', description: 'Discount amount (in addition to normal business discount) at a 100% benefit factor.' },
      { title: 'Overhead costs incurred supporting SED', description: 'Verifiable costs (monetary and non-monetary) at an 80% benefit factor.' },
    ],
  },
  {
    id: 71, layout: 'numbered', module: 4,
    kicker: 'Module 04', title: 'What Contributes to Socioeconomic Development Targets: Human Resource Capacity',
    lead: 'Contributions made in the form of human resource capacity (skills) are recognised at an 80% benefit factor.',
    items: [
      { title: 'Professional services rendered at no cost, supporting SED', description: 'Recognised at the commercial hourly rate of the professional, at 80%.' },
      { title: 'Professional services rendered at a discount, supporting SED', description: 'Recognised on the value of the discount, at 80%.' },
      { title: 'Time of employees productively deployed assisting SED beneficiaries', description: 'Recognised as monthly salary divided by 160, at 80%.' },
    ],
  },
  {
    id: 72, layout: 'numbered', module: 4,
    kicker: 'Module 04', title: 'B-BBEE Audit Checklist: SED',
    items: [
      { title: 'Statutory documents', description: 'Acknowledgement letter from the beneficiary indicating the value of contributions received and the percentage of black people who benefitted. Independent competent person’s report confirming the % of black people who benefitted.' },
      { title: 'Proof for grants', description: 'Proof of payment.' },
      { title: 'Proof for discounts', description: 'Proof of discounts, comparative invoices, etc.' },
      { title: 'Proof for direct costs incurred', description: 'Invoices for costs, proofs of payment.' },
      { title: 'Proof for overheads', description: 'Invoices and proofs of payment for the overheads.' },
      { title: 'Proof for time of employees', description: 'Timesheets, payroll reports to prove salaries.' },
    ],
  },

  // ===========================================================================
  // Module 5: Preferential Procurement
  // ===========================================================================
  {
    id: 73, layout: 'agenda', module: 5,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },
  {
    id: 74, layout: 'intro', module: 5,
    kicker: 'Module 05', title: 'Preferential Procurement',
    lead: 'Who gets your money, and what is their B-BBEE status? The exclusion principle, TMPS calculation, and supplier analysis.',
    outcome: 'By the end you can calculate recognised spend from a supplier’s B-BBEE level, read a Preferential Procurement scorecard, and check a certificate or affidavit for red flags.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 75, layout: 'lead', module: 5,
    kicker: 'Module 05', title: 'What Is Preferential Procurement?',
    lead: 'Preferential Procurement measures how much of your spending goes to B-BBEE-compliant suppliers.',
    body: 'This is where the exclusion principle, the TMPS calculation, and supplier analysis matter. Essentially: who gets your money, and what is their B-BBEE status?',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 76, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Preferential Procurement: Scoring Scale',
    lead: 'The same nine-level scale drives the procurement recognition percentage applied to every supplier’s spend.',
    items: [
      { title: 'Level One Contributor', description: '135% recognition.' },
      { title: 'Level Two Contributor', description: '125% recognition.' },
      { title: 'Level Three Contributor', description: '110% recognition.' },
      { title: 'Level Four Contributor', description: '100% recognition.' },
      { title: 'Level Five Contributor', description: '80% recognition.' },
      { title: 'Level Six Contributor', description: '60% recognition.' },
      { title: 'Level Seven Contributor', description: '50% recognition.' },
      { title: 'Level Eight Contributor', description: '10% recognition.' },
      { title: 'Non-Compliant Contributor', description: '0% recognition.' },
    ],
  },
  {
    id: 77, layout: 'numbered', module: 5,
    kicker: 'Module 05 · Worked example', title: 'Preferential Procurement: Scoring Scale in Rand Terms',
    lead: 'On R100 of spend with a supplier at each level, this is the recognised spend that counts toward your scorecard.',
    items: [
      { title: 'Level 1 Contributor — Robust', description: 'R100 spend x 135% = R135 recognised spend.' },
      { title: 'Level 2 Contributor', description: 'R100 spend x 125% = R125 recognised spend.' },
      { title: 'Level 3 Contributor', description: 'R100 spend x 110% = R110 recognised spend.' },
      { title: 'Level 4 Contributor', description: 'R100 spend x 100% = R100 recognised spend.' },
      { title: 'Level 5 Contributor — Medium', description: 'R100 spend x 80% = R80 recognised spend.' },
      { title: 'Level 6 Contributor', description: 'R100 spend x 60% = R60 recognised spend.' },
      { title: 'Level 7 Contributor — Dilutive', description: 'R100 spend x 50% = R50 recognised spend.' },
      { title: 'Level 8 Contributor', description: 'R100 spend x 10% = R10 recognised spend.' },
      { title: 'Non-Compliant Contributor', description: 'R100 spend x 0% = R0 recognised spend.' },
    ],
  },
  {
    id: 78, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Preferential Procurement: Scorecard',
    lead: 'Total: 27 points plus 2 bonus points.',
    items: [
      { title: 'B-BBEE Procurement Spend from all Empowering Suppliers', description: 'Based on B-BBEE Procurement Recognition Levels, as a % of TMPS: 5 points, 80% target.' },
      { title: 'B-BBEE Procurement Spend from Empowering Suppliers who are QSEs', description: '3 points, 15% target.' },
      { title: 'B-BBEE Procurement Spend from Empowering Suppliers who are EMEs', description: '4 points, 15% target.' },
      { title: 'B-BBEE Procurement Spend from Empowering Suppliers at least 51% black owned', description: '11 points, 50% target.' },
      { title: 'B-BBEE Procurement Spend from Empowering Suppliers at least 30% black women owned', description: '4 points, 12% target.' },
      { title: 'B-BBEE Procurement Spend from Designated Group Suppliers at least 51% black owned', description: '2 bonus points, 2% target.' },
    ],
  },
  {
    id: 79, layout: 'lead', module: 5,
    kicker: 'Module 05', title: 'Preferential Procurement: Policy',
    lead: 'A procurement policy sets year-on-year contributor-level targets and splits suppliers into key and non-key categories.',
    body: 'A typical robust trajectory targets Level 4 in 2025, Level 3 in 2026, and Level 3 in 2027, with pressure to move suppliers toward Black Women Owned status. Percentage-of-total-measured-direct-procurement-spend triggers (such as 5%, 2.8%, 80%, 3% and 7.5%) determine whether a BWO/QSE/EME supplier is retained, ring-fenced and signed off, or fed into supplier development. Where the answer to a robustness check is "No", the action is to ring-fence and sign off, or increase the level base over time (for example 2025 Level 7, 2026 Level 6, 2027 Level 5). Where the answer is "Yes", the action is to collect scorecards on the New Codes and continue with no additional pressure of focus.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 80, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Enhanced Recognition: Procurement Spend',
    lead: 'Three criteria each carry a 1.2x recognition factor on procurement spend.',
    items: [
      { title: 'A Supplier Development beneficiary', description: 'With a minimum 3-year contract with the measured entity: 1.2x.' },
      { title: 'A black-owned QSE or EME, not an SD beneficiary', description: 'But with a minimum 3-year contract with the measured entity: 1.2x.' },
      { title: 'An EME, QSE or Large entity with at least 51% black ownership', description: 'Using the flow-through principle: 1.2x.' },
    ],
  },
  {
    id: 81, layout: 'lead', module: 5,
    kicker: 'Module 05', title: 'Calculating Procurement Score',
    lead: 'The procurement score is built from two components: the supplier’s recognised, weighted spend, and the Total Measured Procurement Spend it is measured against.',
    body: 'Getting the recognised spend right (per level, per enhanced-recognition criteria) is only half the calculation. The other half is getting the denominator right, the Total Measured Procurement Spend, which is what the next slide walks through.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 82, layout: 'comparison', module: 5,
    kicker: 'Module 05', title: 'Calculating the Total Measured Procurement Spend',
    columns: [
      { heading: 'Expense items for inclusion', items: ['Cost of sales', 'Operational expenditure', 'Capital expenditure', 'Labour brokers and independent contractors', 'Pension and medical aid contributions', 'Trade commissions', 'Imports', 'Intra-group procurement'] },
      { heading: 'Expense items for exclusion', items: ['Taxation', 'Salaries, wages, remuneration and emoluments', 'Pass-through third-party procurement', 'Empowerment-related procurement', 'Imports (subject to the localisation conditions on the next slide)', 'Public sector procurement', 'Monopolistic procurement', 'Depreciation, amortisation, accounting entries etc.'] },
    ],
    callout: { label: 'The formula', text: 'Total measured spend = Total inclusions, less total exclusions.' },
  },
  {
    id: 83, layout: 'lead', module: 5,
    kicker: 'Module 05', title: 'Imports & Exclusions',
    lead: 'Imported capital goods or components for value-added production in South Africa can be excluded, provided that there is no existing local production for such capital goods or components, and importing them promotes further value-added production within South Africa.',
    body: 'Imported goods that carry a brand different to the locally produced goods or services, or have different technical specifications, will be excluded, subject to having developed and implemented an enterprise development and supplier development plan for those imported goods and services.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 84, layout: 'lead', module: 5,
    kicker: 'Module 05', title: 'Reading the Paperwork: Certificates and Affidavits',
    lead: 'The primary evidence documents in any procurement verification are the BEE Certificate (for Large / Generic entities) and the Affidavit (for EME and QSE entities).',
    body: 'Both carry specific fields that must be checked line by line, not just glanced at, before a supplier’s recognised spend is relied on.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 85, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Things to Look Out for When Inspecting BEE Certificates',
    items: [
      { title: 'Name, address, VAT or registration number', description: 'Must agree to the invoice received.' },
      { title: 'B-BBEE level corresponds with total points', description: '' },
      { title: 'Do the points tally?', description: 'i.e. do they add up.' },
      { title: 'Date of issue & expiry date', description: 'Within 1 year.' },
      { title: 'SANAS stamp with accreditation number', description: '' },
    ],
  },
  {
    id: 86, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Things to Look Out for When Inspecting Affidavits',
    items: [
      { title: 'Gender & race of the signer does not matter (EME)', description: 'Can be signed by ANY director or shareholder.' },
      { title: 'Enterprise name, registration number and address (EME)', description: 'MUST be completed.' },
      { title: 'Nature of business (EME)', description: 'Must be completed, and is used to assess whether the affidavit was signed under the correct sector code (Generic, Construction, ICT, Financial, etc., all differ).' },
      { title: 'Black ownership % must correspond with the BEE level (QSE)', description: 'e.g. 100% black owned = Level 1, 51% black owned = Level 2.' },
      { title: 'Must be stamped & signed by a commissioner of oath (QSE)', description: '' },
      { title: 'Must be signed and dated by the deponent (QSE)', description: 'The date MUST be the same as that of the commissioner of oath.' },
    ],
  },
  {
    id: 87, layout: 'comparison', module: 5,
    kicker: 'Module 05', title: 'B-BBEE Audit Checklist: Preferential Procurement',
    columns: [
      { heading: 'For each sampled local supplier', items: ['Supplier spend report as printed from the system', 'Valid BEE certificates / affidavits for each sampled supplier', 'Detailed supplier ledgers for each sample', '3 to 5 invoices per sampled supplier', 'Proofs of payment (depending on verification agency)'] },
      { heading: 'For each sampled foreign supplier / import', items: ['Detailed supplier ledgers for each sample', '3 to 5 invoices per sampled supplier', 'Proofs of payment (depending on verification agency)', 'Proofs of currency conversion (if invoice is in foreign currency)', 'Management rep letter confirming reasons for exclusion', 'ESD plan for imported goods and services claimed as an exclusion (not applicable to capital goods)', 'Shipping documents for the sampled invoices'] },
    ],
    callout: { label: 'Statutory documents', text: 'Import certificate (to validate imports), plus the TMPS calculation itself.' },
  },

  // ===========================================================================
  // Module 6: Enterprise and Supplier Development
  // ===========================================================================
  {
    id: 88, layout: 'agenda', module: 6,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },
  {
    id: 89, layout: 'intro', module: 6,
    kicker: 'Module 06', title: 'Enterprise and Supplier Development',
    lead: 'Support to Black-owned SMMEs, particularly those not yet in your supply chain, and to those that already are.',
    outcome: 'By the end you can distinguish Enterprise Development from Supplier Development, name what qualifies as a contribution, and read the combined ESD scorecard.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 90, layout: 'lead', module: 6,
    kicker: 'Module 06', title: 'Enterprise and Supplier Development Definition',
    lead: 'Enterprise Development measures your support to Black-owned SMMEs, particularly those NOT in your supply chain.',
    body: 'To qualify, the beneficiary must be an EME or QSE that is at least 51% Black owned.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 91, layout: 'lead', module: 6,
    kicker: 'Module 06', title: 'The Missing Middle',
    lead: 'Between survivalist enterprises at the small end and Corporate South Africa at the large end sits "the missing middle", the size band of businesses that are the real engines of jobs and growth.',
    body: 'Enterprise and Supplier Development exists to help black-owned businesses cross from survivalist scale into that missing middle.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 92, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'Top 5 Constraints to Entrepreneurship',
    items: [
      { title: 'Skills deficit', description: '' },
      { title: 'Hindering regulations & policies', description: '' },
      { title: 'Limited availability of venture and SME funding', description: '' },
      { title: 'Private & public sector disconnects & trust deficit', description: '' },
      { title: 'Low tolerance of risk & failure', description: '' },
    ],
  },
  {
    id: 93, layout: 'lead', module: 6,
    kicker: 'Module 06', title: 'Enterprise and Supplier Development Criteria',
    lead: 'A beneficiary can be supported provided the Measured Entity assisted them as an ESD beneficiary while they were still a 51% Black-owned EME or QSE.',
    body: 'The Measured Entity can only support them subject to a 5-year cap.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 94, layout: 'cards', module: 6,
    kicker: 'Module 06', title: 'Enterprise Development: Best Practice',
    lead: 'Strategic investment through related value offerings, courtship / joint venturing, and collaboration.',
    columns: 3,
    cards: [
      { title: 'Courting future distributors', description: '' },
      { title: 'Courting future customers', description: '' },
      { title: 'Courting future owners', description: '' },
    ],
  },
  {
    id: 95, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'Supplier Development: Best Practice',
    items: [
      { title: 'Supplier Development plans must have clear objectives', description: '' },
      { title: 'Priority interventions', description: '' },
      { title: 'Key performance indicators', description: '' },
      { title: 'An implementation plan with clearly articulated milestones', description: '' },
      { title: 'No double-dipping', description: 'You cannot "double dip" on supplier and enterprise development, but you could do 3% of NPAT on supplier development and get the full score.' },
      { title: 'Qualifying criteria for eligibility under SD', description: 'Beneficiaries must be ED beneficiaries who are also suppliers, and have had a needs analysis conducted as the basis of engagement.' },
    ],
  },
  {
    id: 96, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'What Type of Contributions Qualify as ESD: Grants and Costs',
    items: [
      { title: 'Grant contribution', description: 'Full grant amount, 100% recognition factor.' },
      { title: 'Direct cost incurred supporting ESD', description: 'Verifiable cost (monetary and non-monetary), 100% recognition factor.' },
      { title: 'Discounts beyond normal business practice, supporting ED/SD beneficiaries', description: 'Discount amount (in addition to normal business discount), 100% recognition factor.' },
      { title: 'Overhead costs supporting ESD (including people appointed in ESD)', description: 'Verifiable costs (monetary and non-monetary), 70% recognition factor.' },
    ],
  },
  {
    id: 97, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'What Type of Contributions Qualify as ESD: Loans and Equity',
    items: [
      { title: 'Interest-free loan with no security requirements, supporting ED/SD', description: 'Recognised on the outstanding loan amount, 70% recognition factor.' },
      { title: 'Standard loan with no security', description: 'Recognised on the outstanding loan amount, 50% recognition factor.' },
      { title: 'Guarantees on behalf of a beneficiary entity, supporting ED/SD', description: 'Recognised on the guarantee amount, 3% recognition factor.' },
      { title: 'Lower interest rate supporting ED/SD', description: 'Recognised on the outstanding loan amount, at the prime rate less the actual rate.' },
      { title: 'Minority investment in ESD', description: 'Recognised on the investment amount, 70% recognition factor.' },
      { title: 'ESD investment with lower dividend to financier', description: 'Recognised on the investment amount, at the dividend rate of ordinary shareholders less the actual dividend rate of the contributor.' },
    ],
  },
  {
    id: 98, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'What Type of Contributions Qualify as ESD: Human Capacity and Cashflow',
    items: [
      { title: 'Professional services rendered at no cost', description: 'Recognised at the commercial hourly rate of the professional, 60% recognition factor.' },
      { title: 'Professional services rendered at a discount', description: 'Recognised on the value of the discount, 60% recognition factor.' },
      { title: 'Time of employees productively deployed assisting beneficiaries', description: 'Recognised as monthly salary divided by 160, 60% recognition factor.' },
      { title: 'Shorter payment periods for Supplier Development beneficiaries', description: 'Percentage of invoiced amount x 15% (an approximation of the cost of short-term funding), less the number of days invoiced from payment; maximum points earnable is 15% of 10 points.' },
    ],
  },
  {
    id: 99, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'Enterprise and Supplier Development Scorecard',
    lead: 'Total: 15 points plus 2 bonus points.',
    items: [
      { title: 'Supplier Development', description: 'Annual value of all Supplier Development contributions as a % of the target: 10.00 points, 2% target.' },
      { title: 'Enterprise Development', description: 'Annual value of all Enterprise Development contributions as a % of the target: 5.00 points, 1% target.' },
      { title: 'Bonus — Graduation', description: 'Graduation of one or more Enterprise Development beneficiaries to the Supplier Development level: 1 bonus point.' },
      { title: 'Bonus — Job Creation', description: 'Job creation for one or more Black people directly resulting from SD and ED initiatives: 1 bonus point.' },
    ],
  },
  {
    id: 100, layout: 'comparison', module: 6,
    kicker: 'Module 06', title: 'B-BBEE Audit Checklist: ESD',
    columns: [
      { heading: 'Statutory documents', items: ['Valid EME or QSE affidavit of the beneficiary, confirming revenue bracket and black ownership (if any)', 'Enterprise Development Agreement or Supplier Development agreement', 'Acknowledgement / confirmation letter from the ED / SD beneficiary', 'Supplier Development: supporting ledgers and invoices proving they are a supplier'] },
      { heading: 'Proof of the support given', items: ['Grant: proof of payment', 'Loan: proof of payment, loan agreement, loan ledger', 'Discounts: proof of discounts, comparative invoices, etc.', 'Direct costs incurred: invoices for costs, proofs of payment', 'Overheads: invoices and proofs of payment', 'Time of employees: timesheets, payroll reports to prove salaries', 'Early payments: invoices and payments paid early'] },
    ],
    callout: { label: 'Bonus points', text: 'Job creation: proof of jobs created (IDs, payslips, employment contracts). Graduation from Enterprise Development to Supplier Development level: proof of support given prior to becoming a supplier, and proof of support post becoming a supplier.' },
  },

  // ===========================================================================
  // Module 7: Skills Development
  // ===========================================================================
  {
    id: 101, layout: 'agenda', module: 7,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },
  {
    id: 102, layout: 'intro', module: 7,
    kicker: 'Module 07', title: 'Skills Development',
    lead: 'How much the company invests in training, upskilling and qualifying Black employees and learners, and how that spend is classified and scored.',
    outcome: 'By the end you can classify a learning programme against the Learning Matrix, read the Skills Development scorecard, and apply the Employment Equity classification rules that sit alongside it.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 103, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'What Is Skills Development?',
    lead: 'Skills Development measures how much the company invests in training, upskilling and qualifying Black employees and learners.',
    body: 'It includes accredited training, learnerships, internships, disabled learner programmes, Category A training, and mandatory training reporting.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 104, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'What Qualifies as Skills Development Spend',
    lead: 'Not all training spend is treated equally, certification, category, and who the training is for all change how it counts.',
    body: 'The next few slides walk through the certification caps, the Learning Matrix categories, and the scorecard those decisions feed into.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 105, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'Certification: Uncertified Training Is Capped',
    lead: 'Certified programmes are recognised against evidence of an invoice.',
    body: 'Informal and workplace learning programmes, or Category F & G learning programmes, are limited to a maximum of 25% of the total skills spend. Legitimate training costs and the costs of employing an SDF and training manager are limited to a maximum of 15% of the total skills spend. Any costs and stipends related to bursaries with a "Higher Institution" are 100% recognised.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 106, layout: 'comparison', module: 7,
    kicker: 'Module 07', title: 'Certification vs Non-Certification',
    columns: [
      { heading: 'Certified', items: ['An independent written certificate as per the "Learning Achievements" column of the Learning Programmes Matrix.', 'An enrolment certificate issued by the independent person confirming the employee is enrolled for, is attending, and is making satisfactory progress in the learning programme.'] },
      { heading: 'Non-certified', items: ['A programme that is not certified.', 'Includes cases where the employee enrolled for but did not attend the learning, or attended but failed an evaluation of their learning progress.'] },
    ],
  },
  {
    id: 107, layout: 'numbered', module: 7,
    kicker: 'Module 07', title: 'How to Classify Learnerships: The Learning Matrix',
    lead: 'Seven categories, each with its own learning site and recognition.',
    items: [
      { title: 'A — Learning institute', description: 'Degree with no workplace involvement, e.g. a university degree. Learning site: institution. Recognition: 100% course fees.' },
      { title: 'B — Workplace experience + learning institute', description: 'Part of a qualification, e.g. an internship. Learning site: mixed. Recognition: 100% course fees + 100% stipend.' },
      { title: 'C — Apprenticeship', description: 'Learning institution, or workplace with accreditation to provide the apprenticeship, SAQA-aligned. Learning site: workplace. Recognition: 100% course fees + 100% stipend.' },
      { title: 'D — Learnership', description: 'Workplace experience required, assessed by SETA / QCTO. Learning site: mixed. Recognition: 100% course fees + 100% stipend.' },
      { title: 'E — Certified external training', description: 'SETA/QCTO or SAQA-registered skills programmes. Learning site: institution. Recognition: 100% course fees.' },
      { title: 'F — Certified internal and uncertified external training', description: 'If the workplace is SETA/QCTO-accredited, otherwise external training. Learning site: mixed. Recognition: 15% of total Skills Development target.' },
      { title: 'G — Uncertified internal training', description: 'On the job. Learning site: internal. Recognition: 15% of total Skills Development target.' },
    ],
  },
  {
    id: 108, layout: 'numbered', module: 7,
    kicker: 'Module 07', title: 'The Skills Development Scorecard',
    lead: 'Total: 20 points plus 5 bonus points.',
    items: [
      { title: 'Expenditure on learning programmes for black people', description: '6 points, 3.5% of payroll target.' },
      { title: 'Expenditure on bursaries for black students at Higher Education Institutions', description: '4 points, 2.5% of payroll target.' },
      { title: 'Expenditure on learning programmes for black disabled people', description: '4 points, 0.3% of payroll target.' },
      { title: 'Number of black people in B, C, D programmes', description: '6 points, 5% of total staff target.' },
      { title: 'Bonus — Absorption', description: 'Absorption of black people after completion of B, C, D programmes: 5 points, 100% of total completed target.' },
    ],
  },
  {
    id: 109, layout: 'comparison', module: 7,
    kicker: 'Module 07', title: 'Economically Active Population (EAP)',
    lead: 'EAP means all persons who furnish the supply of labour for the production of economic goods and services. EAP statistics are applied based on a measured entity’s geographical activity across South Africa.',
    columns: [
      { heading: 'Male EAP', items: ['African male: 43.5%', 'Coloured male: 4.6%', 'Indian male: 1.7%', 'White male: 4.1%'] },
      { heading: 'Female EAP', items: ['African female: 37.5%', 'Coloured female: 4.2%', 'Indian female: 1.0%', 'White female: 3.4%'] },
    ],
    callout: { label: 'Adjusted EAP', text: 'Excluding White male and White female, the Adjusted EAP totals 92.5%.' },
  },
  {
    id: 110, layout: 'keyterm', module: 7,
    kicker: 'Key term', term: 'Person with a Disability',
    definition: 'A person satisfying all criteria in the definition: having a physical or mental impairment which is long term or recurring, and which substantially limits their prospects of entry into, or advancement in, employment.',
    body: 'Physical impairment means a partial or total loss of a bodily function or part of the body, including sensory impairments such as being deaf, hearing impaired, or visually impaired. Mental impairment means a clinically recognized condition or illness that affects a person’s thought processes, judgment or emotions.',
    templateAsset: 'icon-empathy',
    callouts: [
      { label: 'Long-term impairment', text: 'Has lasted or is likely to persist for at least twelve months.' },
      { label: 'Recurring impairment', text: 'Likely to happen again and to be substantially limiting, including a constant chronic condition.' },
      { label: 'Substantially limiting', text: 'In its nature, duration or effects, it substantially limits the person’s ability to perform the essential functions of the job for which they are being considered, not if easily controlled, corrected or lessened (e.g. glasses).' },
    ],
  },
  {
    id: 111, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'Reasonable Accommodation',
    lead: 'Designated employers "should reasonably accommodate the needs of people with disabilities."',
    body: 'The aim is to reduce the impact of the impairment on the person’s capacity to perform the essential functions of the job, and to make it possible for a suitably qualified person with a disability to perform as everyone else. What that accommodation looks like depends on the job and its essential functions, the work environment, and the person’s specific impairment.',
    templateAsset: 'icon-empathy', imageChrome: 'none',
  },
  {
    id: 112, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'Disclosure',
    lead: 'An employee with a disability may choose to disclose their disability, impairment and related accommodation requirements at any time in the employment process.',
    body: 'If a person with a disability chooses NOT to disclose, the employer may not be aware of the needs of the employee, especially if the impairment is not self-evident; in that case, the employer is NOT OBLIGED to provide the accommodation. If the disability is self-evident, the employer can reasonably be expected to be aware and to be proactively involved in identifying with the employee what accommodation may be required.',
    templateAsset: 'icon-empathy', imageChrome: 'none',
  },
  {
    id: 113, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'Employment Equity Strategy: Performance vs Potential',
    lead: 'Employees are plotted on a grid of performance (needs development / meets expectations / exceeds expectations) against potential (limited / growth / high).',
    body: 'High potential paired with exceeding expectations points toward preparing the person for a future role. Limited potential paired with needs-development performance points toward a bad hire that should be replaced. The cells in between call for improving in the current role, reassigning, or reconsidering, depending on where performance and potential intersect.',
    templateAsset: 'conversation-coaching', imageChrome: 'none',
  },
  {
    id: 114, layout: 'numbered', module: 7,
    kicker: 'Module 07', title: 'Employment Equity Strategy: Leadership and Measurement',
    lead: 'All leadership, not just the EE committee, must give this process visibility and teeth.',
    items: [
      { title: 'Future pool', description: 'Operational leaders / Exco meet to review and develop sourcing and succession programs.' },
      { title: 'Current pool', description: 'Operational leaders / Exco meet to review current skills activity, to optimize and strengthen the skills program.' },
      { title: 'Commitment to agreed targets', description: 'EAP-driven targets built into all management decision-making.' },
      { title: 'A talent meeting', description: 'Created to drive the agenda.' },
      { title: 'Hearts and minds', description: 'An employee engagement climate survey linked to transformation, specifically talent management.' },
      { title: 'Clarify the EE vision', description: 'The "want-to-be" condition, with targeted programs to identify future talent, identified and budgeted.' },
      { title: 'Succession planning within management levels', description: 'For equity candidates, including international programme opportunities.' },
      { title: 'Current pool needs', description: 'Are they sufficiently addressed through coaching, mentoring, upskilling, and development programs, with opportunities referred to HR to manage the database?' },
    ],
  },
  {
    id: 115, layout: 'comparison', module: 7,
    kicker: 'Module 07', title: 'B-BBEE Audit Checklist: Skills Development',
    columns: [
      { heading: 'Statutory documents', items: ['EMP201s for the financial period (to validate SDL and payroll)', 'Copy of the Workplace Skills Plan (WSP)', 'Proof of the WSP submission to the relevant SETA', 'Proof of SETA approval of the WSP', 'Management rep letter confirming Mandatory Skills Spend was not claimed', 'Management rep letter confirming % time of employees dedicated to training', '12-month payroll report to confirm CTC of Training Managers / SDF for the year'] },
      { heading: 'For each sampled training', items: ['Certified ID copies of all trainees', 'Invoices and proof of payments relating to the sampled skills spend', 'Proof of attendance (attendance registers or certificates)', 'Copies of bursary contracts / agreements', 'EEA1 and doctor’s disability confirmation letter for disabled employees/learners', 'Learners’ payroll report', 'Learnership agreements and fixed-term contracts, confirmed as registered with SETA', 'Learnership tracking tool', 'Proof of absorption (permanent employment contract)'] },
    ],
  },

  // ===========================================================================
  // Module 8: Management Control
  // ===========================================================================
  {
    id: 116, layout: 'agenda', module: 8,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_WITH_SCENARIO_PLANNING,
  },
  {
    id: 117, layout: 'intro', module: 8,
    kicker: 'Module 08', title: 'Management Control',
    lead: 'Black participation in strategic decision-making, from the board down to junior management, and the Employment Equity process behind it.',
    outcome: 'By the end you can read the full Management Control scorecard, work through an EAP-weighted representation calculation, and place a role on the EEA9 occupational-level grid.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 118, layout: 'lead', module: 8,
    kicker: 'Module 08', title: 'What Is Management Control?',
    lead: 'Management Control evaluates the effective participation of Black people in strategic decision-making positions within the organisation.',
    body: 'It focuses on board participation (Black Executive Directors, Black Non-Executive Directors), control structures such as voting rights and decision authority, Black female representation at governance level, executive management responsible for strategic and operational oversight, and Black persons with disability at leadership level (where applicable).',
    templateAsset: 'icon-professionalism', imageChrome: 'none',
  },
  {
    id: 119, layout: 'numbered', module: 8,
    kicker: 'Module 08', title: 'Management Control Scorecard: Board and Executive',
    lead: 'Subtotal: 9 points.',
    items: [
      { title: 'Voting rights held by black board members', description: '2 points, 50% target.' },
      { title: 'Voting rights held by black female board members', description: '1 point, 25% target.' },
      { title: 'Black executive directors', description: '2 points, 50% target.' },
      { title: 'Black female executive directors', description: '1 point, 25% target.' },
      { title: 'Black executive management', description: '2 points, 60% target.' },
      { title: 'Black female executive management', description: '1 point, 30% target.' },
    ],
  },
  {
    id: 120, layout: 'numbered', module: 8,
    kicker: 'Module 08', title: 'Management Control Scorecard: Senior, Middle and Junior Management',
    lead: 'Subtotal: 10 points. Total across both scorecard slides: 19 points.',
    items: [
      { title: 'Black employees in senior management', description: '2 points, 60% target.' },
      { title: 'Black female employees in senior management', description: '1 point, 30% target.' },
      { title: 'Black employees in middle management', description: '2 points, 75% target.' },
      { title: 'Black female employees in middle management', description: '1 point, 38% target.' },
      { title: 'Black employees in junior management', description: '1 point, 88% target.' },
      { title: 'Black female employees in junior management', description: '1 point, 44% target.' },
      { title: 'Black employees living with disabilities', description: '2 points, 2% target.' },
    ],
  },
  {
    id: 121, layout: 'timeline', module: 8,
    kicker: 'Module 08', title: 'The Employment Equity Process',
    templateAsset: 'take-action',
    steps: [
      { era: 'Phase One', title: 'Preparation', description: 'Develop the EE policy and statement, communicate it, obtain line management buy-in, and establish EE committees.' },
      { era: 'Phase Two', title: 'Audit', description: 'Plan the audits, conduct comprehensive EE audits, compile consolidated findings and recommendations, and submit them to the BEE team.' },
      { era: 'Phase Three', title: 'The EE Plan', description: 'Set goals and draw up the EE plan, submit it to top management, prepare for implementation and its management, and prepare for monitoring and evaluation.' },
    ],
  },
  {
    id: 122, layout: 'lead', module: 8,
    kicker: 'Module 08 · Worked example', title: 'How Management Control Is Measured in RCOGP',
    lead: 'Example: 10 middle managers, all male, one African black, one Indian, one coloured. Note: if representation exceeds EAP it is capped at the EAP; this is not explicit in the RCOGP but is a necessity, otherwise it actively drives people to increase representation with Indians and away from African blacks.',
    body: 'Step 1: ratio of African male middle managers to all middle managers = 1 / 10 = 10%. Step 2: achieved against the effective EAP target (34.0%, being 45% x 75%) = 10% / 34.0% = 29.4%. Step 3: scored against the effective EAP weight (0.9066, being 45% x 2) = 29.4% x 0.9 = 0.27. Add the scores from all 6 categories: 0.27 [African male] + 0.13 [Coloured male] + 0.04 [Indian male] = 0.44 out of a total of 2 for that category.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 123, layout: 'lead', module: 8,
    kicker: 'Module 08', title: 'Management Control Scorecard Summary',
    lead: 'Every category on the Management Control scorecard, board participation through employees with disabilities, is tracked in one summary against target, prior year, actual, and projected columns.',
    body: 'This is the live tracking template teams update each verification cycle, the same categories and targets from the two scorecard slides, carried forward against real, current performance data rather than left as a static once-off calculation.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 124, layout: 'numbered', module: 8,
    kicker: 'Module 08', title: 'EEA9: DOL Grading System Alignment',
    lead: 'The Department of Labour’s EEA9 form aligns five occupational levels against the Patterson grading system (and, in the source data, against ReMeasure, Hay, Peromnes, Task and JEasy point systems too).',
    items: [
      { title: 'Top Management / Executives', description: 'Patterson Classic F+1 to F+5.' },
      { title: 'Senior Management', description: 'Patterson Classic E1 to E5.' },
      { title: 'Professionally Qualified & Experienced Specialist / Mid Management', description: 'Patterson Classic D1 to D5.' },
      { title: 'Skilled Technical & Academically Qualified / Junior Management / Supervisors / Foremen / Superintendents', description: 'Patterson Classic C1 to C5.' },
      { title: 'Semi-Skilled & Discretionary Decision Making', description: 'Patterson Classic B1 to B5.' },
      { title: 'Unskilled & Defined Decision Making', description: 'Patterson Classic A1 to A3.' },
    ],
  },
  {
    id: 125, layout: 'numbered', module: 8,
    kicker: 'Module 08', title: 'EEA9: Occupational Level Descriptions',
    items: [
      { title: 'Top Management / Executives', description: 'Controls the functional integration of the business, determines the overall strategy and objectives, directs the company into the future. Long-term focus, sign-off on policy or strategy. Examples: CEO, CFO, COO, Divisional Directors, Exco.' },
      { title: 'Senior Management', description: 'Knowledge of the entire business area / BU / company or group. Provides input for the overall organisational strategy, translates it into business plans for the BU / functional unit, implements and manages business plans, goals and objectives, and manages the development of innovation and change.' },
      { title: 'Professionally Qualified & Experienced Specialist / Mid Management', description: 'Professional knowledge of a sub-discipline or discipline. Provides input into organisation/functional unit business plans, formulates and implements departmental/team plans supporting the BU business plans, and optimizes resources to achieve objectives productively and cost-efficiently.' },
      { title: 'Skilled Technical & Academically Qualified / Junior Management / Supervisors / Foremen / Superintendents', description: 'Applies broad knowledge of products, techniques and processes, evaluates procedures and applies previous experience. A good solution can usually be found. Determines own priorities; what has to be done is stipulated, but how may require initiative.' },
      { title: 'Semi-Skilled & Discretionary Decision Making', description: 'Accountable for direct product, process or service quality. Incremental improvement of existing processes and procedures according to clear guidelines, choosing the correct action on the basis of set standards, training procedures and past experience.' },
      { title: 'Unskilled & Defined Decision Making', description: 'Steps to accomplish work or processes are clearly defined and understood. Tasks are sometimes repetitive and uncomplicated, and the work cycle is short.' },
    ],
  },
  {
    id: 126, layout: 'cards', module: 8,
    kicker: 'Module 08', title: 'Diversity Is Essential',
    lead: 'Diversity & inclusion is driven by talent, market, innovation & productivity, legislation, and brand reputation.',
    columns: 5,
    cards: [
      { title: 'Talent', description: '' },
      { title: 'Market', description: '' },
      { title: 'Innovation & Productivity', description: '' },
      { title: 'Legislation', description: '' },
      { title: 'Brand Reputation', description: '' },
    ],
    footer: 'National transformation: it’s the right thing to do.',
  },
  {
    id: 127, layout: 'comparison', module: 8,
    kicker: 'Module 08', title: 'B-BBEE Audit Checklist: Management Control',
    columns: [
      { heading: 'Statutory documents', items: ['Latest EEA2 and EEA4 forms (Employment Equity Report Submissions), plus proof of submission', 'Current dated COR39 reflecting the registered company directors', 'Company organogram', 'Memorandum of Incorporation', 'Management rep letter confirming voting rights of directors, executive and other executive management', 'Latest payroll report', 'Latest minutes of Board of Directors and EXCO meetings', 'Non-executive director declarations (for non-executive board members)'] },
      { heading: 'For each sampled employee', items: ['Latest payslips and employment contracts', 'Certified ID copies', 'EEA1 forms (for employees living with disabilities)', 'Doctor’s note / medical certificate (for employees living with disabilities)', 'Employees need to be interviewed by the verification agency'] },
    ],
  },

  // ===========================================================================
  // Module 9: Ownership
  // ===========================================================================
  {
    id: 128, layout: 'agenda', module: 9,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },
  {
    id: 129, layout: 'intro', module: 9,
    kicker: 'Module 09', title: 'Ownership',
    lead: 'The extent to which Black people hold equity, voting rights and economic interest, real participation, not just share certificates.',
    outcome: 'By the end you can read the Ownership scorecard, explain Net Value, and compare the four common transaction structures used to build black ownership.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 130, layout: 'lead', module: 9,
    kicker: 'Module 09', title: 'What Is Ownership?',
    lead: 'Ownership measures the extent to which Black people hold equity, voting rights and economic interest in a company.',
    body: 'It assesses real participation, not just share certificates, including how value flows to Black shareholders. The turnover threshold for qualifying as a Black New Entrant is R20 million.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 131, layout: 'numbered', module: 9,
    kicker: 'Module 09', title: 'Ownership Scorecard',
    lead: 'Total: 25 points.',
    items: [
      { title: 'Voting rights — Black people', description: '4 points, target 25% + 1 vote. The right to participate in shareholder decision-making.' },
      { title: 'Voting rights — Black women', description: '2 points, target 10%.' },
      { title: 'Economic interest — Black people', description: '4 points, target 25%. The right to participate in the wealth of the company, i.e. to receive a dividend and share in capital gains.' },
      { title: 'Economic interest — Black women', description: '2 points, target 10%.' },
      { title: 'Economic interest — Black Designated Groups or ownership-scheme participants', description: '3 points, target 3%.' },
      { title: 'Economic interest — Black New Entrants', description: '2 points, target 2%.' },
      { title: 'Realisation points — Net Value', description: '8 points, target 100%. Wealth creation by Black people.' },
    ],
  },
  {
    id: 132, layout: 'numbered', module: 9,
    kicker: 'Module 09', title: 'Net Value: Three Fundamental Aspects of Ownership',
    items: [
      { title: 'A say on shareholder decisions', description: 'The right to have a say on shareholder decisions.' },
      { title: 'Participation in economic interest', description: 'The right to participate in the economic interest of the company, i.e. the right to receive a dividend.' },
      { title: 'Wealth creation through ownership', description: 'Whether the value of the underlying equity exceeds outstanding acquisition debt.' },
    ],
    footnote: 'As a Priority Element, the Ownership sub-minimum compliance target is 40% of the Net Value points.',
  },
  {
    id: 133, layout: 'cards', module: 9,
    kicker: 'Module 09', title: 'Transaction Structures for B-BBEE Ownership',
    columns: 4,
    cards: [
      { title: 'Broad-Based Trusts (BBOS)', description: 'Share ownership by a trust set up to fund targeted initiatives (educational or charitable).' },
      { title: 'Employee Trusts (ESOPs)', description: 'Share ownership by a trust for the benefit of employees and/or senior managers.' },
      { title: 'BEE Investors', description: 'Share ownership by Black investment companies, consortiums and/or operational partners.' },
      { title: 'Sale of Assets', description: 'Recognition of BEE ownership points through, e.g., sale of an asset or business to a Black buyer.' },
    ],
  },
  {
    id: 134, layout: 'numbered', module: 9,
    kicker: 'Module 09', title: 'Net Value Drivers',
    items: [
      { title: 'Equity value of the Measured Entity', description: '' },
      { title: 'Outstanding BEE debt', description: '' },
      { title: 'BEE ownership %', description: '' },
      { title: 'Transaction age', description: '' },
    ],
  },
  {
    id: 135, layout: 'lead', module: 9,
    kicker: 'Module 09', title: 'Determination of Net Value',
    lead: 'Net Value is determined as the lower of two competing measures of the black participants’ stake.',
    body: 'The Net Value Drivers on the previous slide, equity value, outstanding BEE debt, BEE ownership % and transaction age, all feed into that determination.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 136, layout: 'numbered', module: 9,
    kicker: 'Module 09', title: 'Net Value Points: Avoiding the Discounting of Black Shareholding',
    lead: 'A time-based graduation factor sets, for each year since the "Current Equity Interest Date", what percentage of Black economic interest must be unencumbered to earn full Formula A points and to meet the 40% sub-minimum.',
    items: [
      { title: 'Year 1', description: '10% of Black economic interest unencumbered; 2.50% needed for full Formula A points; 1% needed for the 40% sub-minimum.' },
      { title: 'Year 2', description: '20% unencumbered; 5% for full Formula A points; 2% for the 40% sub-minimum.' },
      { title: 'Years 3–4', description: '40% unencumbered; 10% for full Formula A points; 4% for the 40% sub-minimum.' },
      { title: 'Years 5–6', description: '60% unencumbered; 15% for full Formula A points; 6% for the 40% sub-minimum.' },
      { title: 'Years 7–8', description: '80% unencumbered; 20% for full Formula A points; 8% for the 40% sub-minimum.' },
      { title: 'Years 9–10', description: '100% unencumbered; 25% for full Formula A points; 10% for the 40% sub-minimum.' },
    ],
  },
  {
    id: 137, layout: 'lead', module: 9,
    kicker: 'Module 09 · Option 1', title: 'Broad-Based Trust Model (Educational or Charitable)',
    lead: 'Share ownership by a Trust set up to fund targeted initiatives, e.g. supporting education and skills development of Black people. Existing shareholders hold 75%, the Broad-Based Trust holds 25%.',
    body: 'Acquisition of shares is funded through a loan from the company (a real loan or notional vendor-financed share). Of the annual dividends flowing to the structure, roughly 20% flows to the BBOS Trust and the remainder (roughly 80%) pays down the loan. BBOS funds are applied to charitable contributions, school fees, scholarships, and training.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 138, layout: 'comparison', module: 9,
    kicker: 'Module 09 · Option 1', title: 'Broad-Based Trust Model: Pros and Cons',
    columns: [
      { heading: 'Pros', items: ['High flexibility and various implementation options', 'Can drive ROI by developing future pipelines of skilled Black people', 'Opportunity to strengthen relations with communities within which the company operates', 'No equity contribution required', 'Commonly implemented and understood'] },
      { heading: 'Cons', items: ['BEE Commission risk, concerns around involvement and identification of beneficiaries', 'Admin and governance support required'] },
    ],
  },
  {
    id: 139, layout: 'lead', module: 9,
    kicker: 'Module 09 · Option 2', title: 'Employee Trust Model',
    lead: 'Share ownership by a Trust for the benefit of employees and/or senior managers. Existing shareholders hold 75%, the Employee Trust holds 25%, and it should include all employees.',
    body: 'Acquisition of shares is funded through a loan from the company. Of annual dividends, roughly 20% flows to the ESOP Trust and the remainder (roughly 80%) pays down the loan. ESOP funds go to employees, participating on the basis of length of service and/or seniority. On wind-up, the ESOP Trust receives the value of 25% of equity value less the outstanding loan.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 140, layout: 'comparison', module: 9,
    kicker: 'Module 09 · Option 2', title: 'Employee Trust Model: Pros and Cons',
    columns: [
      { heading: 'Pros', items: ['High flexibility and various implementation options', 'Opportunity to incentivise management and employees', 'Entrenched empowerment approach, strongly supported by trade unions', 'Opportunity to strengthen employee relations', 'Commonly implemented and understood'] },
      { heading: 'Cons', items: ['Managing employee expectations', 'Admin and governance support required', 'Employee demographics may not yield optimal BEE recognition'] },
    ],
  },
  {
    id: 141, layout: 'comparison', module: 9,
    kicker: 'Module 09 · Option 3', title: 'BEE Investor',
    columns: [
      { heading: 'Pros', items: ['Can build relations and add strategic value', 'May bring an equity contribution, although unlikely', 'Commonly implemented and understood', 'Robust: aligns with DTI and government’s intent and drives active Black Ownership'] },
      { heading: 'Cons', items: ['Longer to negotiate and implement (estimated 6–12 months)', 'Strategic value may be limited', 'Potential conflict regarding strategic or cultural fit'] },
    ],
    callout: { label: 'Description', text: 'Share ownership by Black investment companies/consortiums and/or operational partners. Shareholding can be funded by a combination of own equity and vendor financing, though operational shareholders may have less ability to contribute equity. Ownership by a private equity fund may be treated as ownership by black people when certain criteria are met.' },
  },
  {
    id: 142, layout: 'comparison', module: 9,
    kicker: 'Module 09 · Option 4', title: 'Sale of Assets',
    columns: [
      { heading: 'Pros', items: ['No shareholder dilution of existing shareholders', 'BEE recognition and scoring locked-in after year three', 'Possible creation of a Black supplier', 'Typically lower cost than an equity sale'] },
      { heading: 'Cons', items: ['Disposal of the asset at a discount', 'Possible non-control of productive assets', 'Relative complexity', 'Limited flexibility'] },
    ],
    callout: { label: 'Description', text: 'Recognition of BEE ownership points through qualifying transactions (sale of an asset, business, or equity instruments), calculated with reference to the value of the business unit or property sold relative to the seller’s total equity value, and the purchaser’s BEE ownership credentials. BEE recognition and points are locked in based on relative values at the end of the first 3 years, and an independent expert must review the transaction. Clients often sell a property to a Black investor and lease it back for 7–10 years (a "sale and lease-back" transaction).' },
  },
  {
    id: 143, layout: 'numbered', module: 9,
    kicker: 'Module 09', title: 'B-BBEE Audit Checklist: Ownership',
    items: [
      { title: 'Share certificates and register', description: 'Copy of latest share certificates and the latest share register.' },
      { title: 'Shareholders agreements', description: 'Copy of shareholders agreements, plus confirmation of voting rights if not stated in the agreement.' },
      { title: 'Trust deed', description: 'Copy of the trust deed of any trust that holds equity in the business, plus a written record of the names of beneficiaries.' },
      { title: 'Sale / subscription agreement', description: 'Copy of the sale of shares agreement / subscription agreement, depending on the nature of the acquisition.' },
      { title: 'Shareholder identity', description: 'Copies of all shareholders’ IDs, and a statement per Black New Entrant confirming their status as such.' },
      { title: 'Net Value evidence', description: 'A valuation of the company at the date of the last financial year (or latest), and proof of payment for any liabilities owed by Black shareholders for the financing of their share purchase (loans).' },
      { title: 'Sale of Assets evidence', description: 'Valuation of the company, the assets sold, and any outstanding acquisition debt.' },
    ],
  },

  // ===========================================================================
  // Module 10: YES4Youth
  // ===========================================================================
  {
    id: 144, layout: 'agenda', module: 10,
    kicker: 'Agenda', title: 'Agenda',
    columns: AGENDA_COLUMNS_CORE,
  },
  {
    id: 145, layout: 'intro', module: 10,
    kicker: 'Module 10', title: 'YES4Youth',
    lead: 'The Youth Employment Service programme: qualifying criteria, recognition levels, headcount targets, costs and conditions.',
    outcome: 'By the end you can check whether a company qualifies as a YES organisation, calculate its headcount target, and explain what level movement it can earn.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 146, layout: 'lead', module: 10,
    kicker: 'Module 10', title: 'Youth Employment Service (YES)',
    lead: 'YES is a scorecard-linked programme that rewards companies for employing, training and paying young South Africans on fixed-term contracts.',
    body: 'The rest of this module works through who qualifies as a YES organisation, how the headcount target is set, and what B-BBEE level movement participation can earn.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 147, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'Are You a YES Organisation?',
    lead: 'Two conditions must both be met to qualify.',
    items: [
      { title: 'Priority element compliance', description: 'Have you met at least 40% of the sub-minimum on the priority elements, OR an average of 50% across the priority elements?' },
      { title: 'Maintain your level', description: 'Maintain your previous B-BBEE level or better.' },
    ],
  },
  {
    id: 148, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'YES Program: Target and Recognition Outcomes',
    lead: 'The YES target is the higher of 1.5% of the previous year’s employee headcount, or 1.5% of the average NPAT for the last 3 years, divided by R55,000 (QSEs use the headcount table only).',
    items: [
      { title: 'Achieve the YES target + 2.5% absorption', description: 'Move up 1 B-BBEE level.' },
      { title: 'Achieve double the YES target + 5% absorption', description: 'Move up 2 B-BBEE levels.' },
      { title: 'Achieve 1.5x the YES target + 5% absorption', description: 'Move up 1 B-BBEE level, plus 3 bonus points.' },
    ],
  },
  {
    id: 149, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'YES Candidates Headcount Per Revenue Band: Generic',
    lead: 'Minimum black youth jobs required, by total revenue band (R million).',
    items: [
      { title: 'R50–75 million', description: '6 minimum black youth jobs.' },
      { title: 'R75–99 million', description: '7 minimum black youth jobs.' },
      { title: 'R100–149 million', description: '8 minimum black youth jobs.' },
      { title: 'R150–199 million', description: '9 minimum black youth jobs.' },
      { title: 'R200–249 million', description: '10 minimum black youth jobs.' },
      { title: 'R250–299 million', description: '11 minimum black youth jobs.' },
      { title: 'R300–349 million', description: '12 minimum black youth jobs.' },
      { title: 'R350–399 million', description: '13 minimum black youth jobs.' },
      { title: 'R400–449 million', description: '14 minimum black youth jobs.' },
      { title: 'R450–500+ million', description: '15 minimum black youth jobs.' },
    ],
  },
  {
    id: 150, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'YES Candidates Headcount Per Headcount Band: QSE / EME',
    lead: 'Minimum black youth jobs required, by employee headcount band.',
    items: [
      { title: '1–19 employees', description: '1 minimum black youth job.' },
      { title: '20–39 employees', description: '2 minimum black youth jobs.' },
      { title: '40–59 employees', description: '3 minimum black youth jobs.' },
      { title: '60–79 employees', description: '4 minimum black youth jobs.' },
      { title: '80–99 employees', description: '5 minimum black youth jobs.' },
      { title: '100–119 employees', description: '6 minimum black youth jobs.' },
      { title: '120–139 employees', description: '7 minimum black youth jobs.' },
      { title: '140–159 employees', description: '8 minimum black youth jobs.' },
      { title: '160–179 employees', description: '9 minimum black youth jobs.' },
      { title: '180–199 employees', description: '10 minimum black youth jobs.' },
    ],
  },
  {
    id: 151, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'YES Program Qualifying Criteria',
    lead: 'Qualification requires meeting either a 40% sub-minimum on every priority element, or a 50% average (35 of 70 points) across all of them.',
    items: [
      { title: 'Ownership net value', description: 'Max 8 points; 40% threshold = 3.2 points.' },
      { title: 'Skills Development', description: 'Max 20 points; 40% threshold = 8.0 points.' },
      { title: 'Preferential Procurement', description: 'Max 27 points; 40% threshold = 10.8 points.' },
      { title: 'Supplier Development', description: 'Max 10 points; 40% threshold = 4.0 points.' },
      { title: 'Enterprise Development', description: 'Max 5 points; 40% threshold = 2.0 points.' },
      { title: 'Total / Average', description: 'Total max 70 points; 50% average threshold = 35 points.' },
    ],
    footnote: 'Qualification also requires confirming the B-BBEE level for the year prior to first implementing YES, and for the measured financial year.',
  },
  {
    id: 152, layout: 'lead', module: 10,
    kicker: 'Module 10', title: 'Costs for YES Initiative Implementation',
    lead: 'Two of the cost components are fixed per candidate: the registration fee per YES candidate is R5,000, and the annual stipend per YES candidate is R55,000.',
    body: 'The estimated annual cost for a given intake is built from the number of YES candidates x these unit costs, plus the company registration fee, recruitment cost, and (where applicable) the host cost for candidates hosted by a YES partner.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 153, layout: 'comparison', module: 10,
    kicker: 'Module 10', title: 'YES Program Employment Conditions (Section 11.6)',
    columns: [
      { heading: 'Conditions', items: ['Entities must abide by South African legislation governing basic conditions of employment and working conditions.', 'YES entities are required to sign fixed-term or temporary employment contracts (minimum 12 months).', 'Positions must be new to the entity and provide full-time work experience over the 12-month contract.', 'Entities must pay the YES candidates.', 'YES entities must commit to ensuring a quality work experience, insofar as the entity is able to provide it.', 'If the candidate cannot be employed after contract expiry, clear communication must be provided to the employee.', 'On completion, YES candidates can be absorbed outside of the business and this will still count as absorption.'] },
      { heading: 'Implications', items: ['YES candidates could create a good reference for themselves and be employable elsewhere or by the entity.', 'Potential for unemployment reduction.', 'Tight performance management requirements.', 'Potential for unfair labour practices if not tightly monitored (CCMA cases & expenses).'] },
    ],
  },
  {
    id: 154, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'YES Program Summary',
    items: [
      { title: 'Employ young people for 12 months', description: '' },
      { title: 'Train and pay them', description: '' },
      { title: 'Assess them during the year', description: '' },
      { title: 'Absorb some at the end of the year', description: '' },
      { title: 'Get audited', description: '' },
      { title: 'Move up 1 or 2 levels on your scorecard', description: '' },
    ],
  },
  {
    id: 155, layout: 'numbered', module: 10,
    kicker: 'Module 10', title: 'B-BBEE Audit Checklist: YES Programme',
    items: [
      { title: 'Statutory documents', description: 'YES registration certificate, proof of payment for the YES registration / invoice, CEO pledge, confirmation of registration and placement start date with the YES Non-Profit Company.' },
      { title: 'BEE history', description: 'Previous BEE certificate and report (where applicable), proof of non-displacement of current B-BBEE initiatives and maintenance of B-BBEE status levels.' },
      { title: 'Financial and headcount evidence', description: 'Signed annual / audited financial statements for the last 3 years (to calculate NPAT targets), employee list as at the measurement date, and payroll report closest to the measurement date (to calculate headcount targets).' },
      { title: 'Employment Equity submissions', description: 'EEA2 and EEA4 reports and proof of submission to the Department of Labour (where applicable).' },
      { title: 'Proof of the support given', description: 'Certified copies of IDs of Black Youth, samples of signed employee / workplace contracts, samples of payslips, and unemployment declarations.' },
    ],
  },

  // ---------------------------------------------------------------------
  // Closing
  // ---------------------------------------------------------------------
  {
    id: 156, layout: 'outcome',
    kicker: 'Course outcome', title: 'What You Can Do Now',
    checks: [
      'Read the Generic Codes scorecard and place a company on the B-BBEE recognition-level table',
      'Calculate Preferential Procurement recognised spend and the Total Measured Procurement Spend',
      'Work through Enterprise & Supplier Development and Socio-Economic Development contribution recognition factors',
      'Read the Skills Development, Management Control and Ownership scorecards, including Net Value and modified flow-through',
      'Check whether a company qualifies as a YES organisation and what level movement it can earn',
    ],
    footnote: 'Contact Us — Website: www.okiru.co.za · Phone: +27 78 104 6527 · E-mail: contact@okiru.co.za',
  },
];
